import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Trophy, ShieldQuestion, Ruler, Weight, Activity, Film, PlayCircle, BarChart3, HeartPulse, Lock } from 'lucide-react';
import { fetchPublicPlayerProfile, friendlyError, buildAssetUrl, fetchPlayerIntelligenceLatest } from '@/lib/api';
import { VideoDownloadInline } from '@/components/downloads/VideoDownloadInline';
import { resolvePlayerAvatar } from '@/lib/playerAvatar';
import { PlayerIntelligencePerformance } from '@/components/players/PlayerIntelligencePerformance';

const SIDE_LABEL = { right: 'Derecho', left: 'Izquierdo', both: 'Ambidiestro' };

export default function PlayerPublicProfile() {
  const { clubSlug, teamSlug, playerSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPublicPlayerProfile(clubSlug, teamSlug, playerSlug)
      .then((d) => { if (alive) setData(d); })
      .catch((err) => { if (alive) setError(friendlyError(err)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [clubSlug, teamSlug, playerSlug]);

  // Fase 3A — carga del último análisis autorizado para el jugador
  // públicamente visible. Se dispara sólo cuando la ficha pública ha
  // resuelto (`data.id` es el `player_profile_id`). Si la API falla,
  // el componente muestra su estado vacío estándar (sin fallback).
  const [publicLatest, setPublicLatest] = useState(null);
  useEffect(() => {
    const pid = data?.id;
    if (!pid) { setPublicLatest(null); return undefined; }
    let alive = true;
    fetchPlayerIntelligenceLatest(pid)
      .then((res) => { if (alive) setPublicLatest(res || null); })
      .catch(() => { if (alive) setPublicLatest(null); });
    return () => { alive = false; };
  }, [data?.id]);
  const publicPlayerIntelligence = React.useMemo(() => {
    if (!publicLatest || !publicLatest.normalized_data) return { player_matches: [] };
    // Conservar el id real del player_match para que el CTA de análisis
    // completo navegue a /jugadores/:playerProfileId/analisis/:matchId.
    return { player_matches: [{ ...publicLatest.normalized_data, player_match_id: publicLatest.id }] };
  }, [publicLatest]);

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-48 mb-6" />
          <Skeleton className="h-80 w-full rounded-2xl" />
        </div>
      </MainLayout>
    );
  }

  if (error || !data) {
    return (
      <MainLayout>
        <div data-testid="player-public-error" className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <ShieldQuestion className="h-12 w-12 text-[var(--ns-text-muted)] mx-auto mb-4" />
          <h1 className="font-display text-2xl text-[var(--ns-text)] mb-2">Este perfil no está disponible para visualización pública.</h1>
          <p className="text-sm text-[var(--ns-text-muted)] mb-6">
            Puede que el club o el administrador no haya autorizado su consulta pública.
          </p>
          <Button asChild variant="outline" className="rounded-xl">
            <Link to={`/clubes/${clubSlug}/equipos/${teamSlug}`}>← Volver a la plantilla</Link>
          </Button>
        </div>
      </MainLayout>
    );
  }

  const p = data;
  const avatar = resolvePlayerAvatar({ profile_photo: p.profile_photo, gender: p.gender });
  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Link
            to={`/clubes/${clubSlug}/equipos/${teamSlug}`}
            className="inline-flex items-center gap-1 text-sm text-[var(--ns-text-muted)] hover:text-[var(--ns-gold-700)]"
          >
            <ArrowLeft className="h-4 w-4" /> Volver a la plantilla
          </Link>
        </div>

        <Card data-testid="player-public-card" className="p-6 sm:p-8 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-shrink-0">
              <div className="h-48 w-48 rounded-2xl overflow-hidden bg-[var(--ns-bg-2)] border border-[var(--ns-border)]">
                {avatar ? (
                  <img src={avatar} alt={p.full_name} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <Trophy className="h-12 w-12 text-[var(--ns-text-subtle)]" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 data-testid="player-public-name" className="font-display text-2xl sm:text-3xl text-[var(--ns-text)]">{p.full_name}</h1>
              <p className="text-sm text-[var(--ns-text-muted)] mt-1">
                {[p.age != null ? `${p.age} años` : null, p.team_category, p.team_gender, p.position].filter(Boolean).join(' · ')}
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {p.sport_name ? <Badge className="bg-[var(--ns-amber-50)] text-[var(--ns-gold-700)] uppercase text-[10px]">{p.sport_name}</Badge> : null}
                {p.club_name ? <Badge variant="outline" className="text-[10px]">{p.club_name}</Badge> : null}
                {p.team_name ? <Badge variant="outline" className="text-[10px]">{p.team_name}</Badge> : null}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
                <StatBox icon={Activity} label="Lado dominante" value={SIDE_LABEL[p.dominant_side] || '—'} testId="player-public-side" />
                <StatBox icon={Ruler} label="Altura" value={p.height_cm ? `${p.height_cm} cm` : '—'} testId="player-public-height" />
                <StatBox icon={Weight} label="Peso" value={p.weight_kg ? `${p.weight_kg} kg` : '—'} testId="player-public-weight" />
              </div>
              {p.bio ? (
                <article className="mt-6">
                  <h2 className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold mb-2">Biografía deportiva</h2>
                  <p data-testid="player-public-bio" className="text-sm text-[var(--ns-text)] leading-relaxed whitespace-pre-line">{p.bio}</p>
                </article>
              ) : null}
            </div>
          </div>
        </Card>

        <Card data-testid="player-public-highlights" className="p-5 sm:p-6 rounded-2xl mt-6 bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-4 w-4 text-[var(--ns-gold-600)]" />
            <h2 className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">Highlights</h2>
            {Array.isArray(p.highlights) && p.highlights.length > 0 ? (
              <Badge className="bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)] text-[10px] uppercase">
                {p.highlights.length}
              </Badge>
            ) : null}
          </div>
          {Array.isArray(p.highlights) && p.highlights.length > 0 ? (
            <ul data-testid="player-public-highlights-list" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {p.highlights.map((h) => {
                const eventDate = h.event_date ? new Date(h.event_date).toLocaleDateString('es-ES', {
                  day: '2-digit', month: 'short', year: 'numeric',
                }) : null;
                const accessBadge = h.video_access_type === 'free'
                  ? { label: 'Gratis', cls: 'bg-emerald-100 text-emerald-800' }
                  : h.video_access_type === 'premium'
                  ? { label: 'Premium', cls: 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]' }
                  : (h.video_access_type === 'pay_per_content' || h.video_access_type === 'premium_or_pay_per_content')
                  ? { label: 'Pago individual', cls: 'bg-sky-100 text-sky-800' }
                  : null;
                return (
                  <li
                    key={h.video_id}
                    data-testid={`player-public-highlight-${h.video_id}`}
                    className="flex flex-col rounded-xl bg-white border border-[var(--ns-border)] overflow-hidden"
                  >
                    <div className="relative aspect-video bg-[var(--ns-bg-2)]">
                      {h.video_thumbnail ? (
                        <img src={buildAssetUrl(h.video_thumbnail)} alt={h.video_title} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-[var(--ns-text-subtle)]">
                          <Film className="h-8 w-8" />
                        </div>
                      )}
                      {accessBadge ? (
                        <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-md text-[10px] uppercase font-semibold ${accessBadge.cls}`}>
                          {accessBadge.label}
                        </span>
                      ) : null}
                    </div>
                    <div className="p-3 flex-1 flex flex-col gap-1.5">
                      <p className="text-sm font-semibold text-[var(--ns-text)] truncate">{h.video_title}</p>
                      <p className="text-xs text-[var(--ns-text-muted)] truncate">
                        {h.event_name}{eventDate ? ` · ${eventDate}` : ''}
                      </p>
                      {h.action_type ? (
                        <p className="text-[11px] text-[var(--ns-text-subtle)]">
                          Acción: <span className="font-semibold text-[var(--ns-text)]">{h.action_type}</span>
                        </p>
                      ) : null}
                      {h.event_slug ? (
                        <Button
                          asChild
                          size="sm"
                          data-testid={`player-public-highlight-play-${h.video_id}`}
                          className="mt-2 h-8 rounded-lg bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] text-xs font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0"
                        >
                          <Link to={`/eventos/${h.event_slug}`} className="inline-flex items-center gap-1">
                            <PlayCircle className="h-3.5 w-3.5" /> Abrir en el partido
                          </Link>
                        </Button>
                      ) : null}
                      {/* Fase 06D · Descarga del contenido oficial (mismo video_id). Silencio si no aplica. */}
                      <VideoDownloadInline videoId={h.video_id} compact />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div data-testid="player-public-highlights-empty" className="p-6 rounded-xl border border-dashed border-[var(--ns-border-strong)] bg-[var(--ns-bg-2)]/40 text-center">
              <Film className="h-8 w-8 text-[var(--ns-text-subtle)] mx-auto mb-2" />
              <p className="text-sm text-[var(--ns-text-muted)]">
                Este jugador aún no tiene highlights oficiales publicados.
              </p>
            </div>
          )}
          <p className="text-[10px] text-[var(--ns-text-subtle)] mt-3">
            Al reproducir un highlight se abre el partido oficial en NetScouters. Se aplican las reglas de acceso del contenido (registro, Premium o compra individual).
          </p>
        </Card>

        {/*
          Sección "Rendimiento" — SIEMPRE visible en toda ficha de jugador,
          independientemente del rol o vinculación del visitante.

          Fase 1 Player Intelligence:
            • La sub-sección "Estadísticas" (placeholder original) se sustituye
              por el componente reutilizable `PlayerIntelligencePerformance`,
              que consume el contrato `{ player_matches: [...] }`.
            • El componente NO contiene datos de ningún jugador. Mientras no
              exista importador ni colección `player_match`, los datos vienen
              del fixture de desarrollo (`mario_player_intelligence.js`) sólo
              cuando la ficha abierta es la de Mario. Cualquier otro jugador
              recibe `player_matches: []` (estado A del componente).
            • La sub-sección "Análisis biomecánico" NO se modifica.
        */}
        {/*
          Fase 3A — Player Intelligence en la ficha pública. Se carga
          desde la API real usando el `player_profile_id` que ya devuelve
          el endpoint público (`data.id`). El servidor autoriza por
          PÚBLICO | PROPIETARIO | ADMIN antes de devolver el payload.
          Sin fallback runtime a fixtures.
        */}
        <PlayerIntelligencePerformance
          data={publicPlayerIntelligence}
        />
        <Card
          data-testid="player-public-performance"
          className="p-5 sm:p-6 rounded-2xl mt-6 bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
        >
          <div className="flex items-center gap-2 mb-4">
            <Activity className="h-4 w-4 text-[var(--ns-gold-600)]" />
            <h2 className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
              Rendimiento
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <PerformanceOption
              icon={HeartPulse}
              title="Análisis biomecánico"
              description="Sección privada. Datos de salud y biomecánica del jugador."
              testId="player-public-performance-biomechanics"
            />
          </div>
          <p className="text-[10px] text-[var(--ns-text-subtle)] mt-3">
            El acceso al detalle se rige por los permisos y accesos ya definidos para cada tipo de contenido.
          </p>
        </Card>
      </div>
    </MainLayout>
  );
}

/**
 * Opción de rendimiento SIEMPRE visible. Muestra bloqueado/no disponible
 * cuando aún no hay datos, sin ocultar la tarjeta.
 */
function PerformanceOption({ icon: Icon, title, description, testId }) {
  return (
    <div
      data-testid={testId}
      className="relative flex flex-col gap-3 p-4 rounded-xl border border-dashed border-[var(--ns-border-strong)] bg-[var(--ns-amber-50)]/30"
    >
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </div>
        <Badge
          className="bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] text-[10px] uppercase inline-flex items-center gap-1"
          data-testid={`${testId}-locked`}
        >
          <Lock className="h-3 w-3" /> No disponible
        </Badge>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-[var(--ns-text)] uppercase tracking-wide">
          {title}
        </h3>
        <p className="text-xs text-[var(--ns-text-muted)] mt-1 leading-relaxed">
          {description}
        </p>
      </div>
      <p className="text-[11px] text-[var(--ns-text-subtle)]">
        Sin datos disponibles todavía.
      </p>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="p-3 rounded-xl border border-[var(--ns-border)] bg-white">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <p className="text-sm font-semibold text-[var(--ns-text)] mt-1">{value}</p>
    </div>
  );
}
